import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ScrollToTop } from "./components/ScrollToTop";

// Layout (always loaded)
import { Layout } from "./components/Layout";
import { CookieBanner } from "./components/CookieBanner";
const AdminLayout = lazy(() => import("./components/AdminLayout").then(m => ({ default: m.AdminLayout })));

// Context (always loaded)
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

// Loading fallback
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#3D2B1F] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Lazy-loaded pages
const HomePage = lazy(() => import("./pages/HomePage").then(m => ({ default: m.HomePage })));
const AboutPage = lazy(() => import("./pages/AboutPage").then(m => ({ default: m.AboutPage })));
const TreatmentsPage = lazy(() => import("./pages/TreatmentsPage").then(m => ({ default: m.TreatmentsPage })));
const BookingPage = lazy(() => import("./pages/BookingPage").then(m => ({ default: m.BookingPage })));
const ShopPage = lazy(() => import("./pages/ShopPage").then(m => ({ default: m.ShopPage })));
const ExclusiveShopPage = lazy(() => import("./pages/ExclusiveShopPage").then(m => ({ default: m.ExclusiveShopPage })));
const BlogPage = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage").then(m => ({ default: m.BlogPostPage })));
const ContactPage = lazy(() => import("./pages/ContactPage").then(m => ({ default: m.ContactPage })));
const CartPage = lazy(() => import("./pages/CartPage").then(m => ({ default: m.CartPage })));
const ShopSuccessPage = lazy(() => import("./pages/ShopSuccessPage").then(m => ({ default: m.ShopSuccessPage })));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage").then(m => ({ default: m.ImpressumPage })));
const AGBPage = lazy(() => import("./pages/AGBPage").then(m => ({ default: m.AGBPage })));
const BrandsPage = lazy(() => import("./pages/BrandsPage").then(m => ({ default: m.BrandsPage })));
const TreatmentInfoPage = lazy(() => import("./pages/TreatmentInfoPage").then(m => ({ default: m.TreatmentInfoPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("./pages/RegisterPage").then(m => ({ default: m.RegisterPage })));

// Admin (heavy - lazy loaded)
const AdminLoginPage = lazy(() => import("./pages/admin/AdminLoginPage").then(m => ({ default: m.AdminLoginPage })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/ueber-mich" element={<AboutPage />} />
                <Route path="/behandlungen" element={<TreatmentsPage />} />
                <Route path="/termin-buchen" element={<BookingPage />} />
                <Route path="/termin-buchen/:treatmentId" element={<BookingPage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/shop/exklusiv" element={<ExclusiveShopPage />} />
                <Route path="/shop/warenkorb" element={<CartPage />} />
                <Route path="/shop/success" element={<ShopSuccessPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/kontakt" element={<ContactPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/agb" element={<AGBPage />} />
                <Route path="/marken" element={<BrandsPage />} />
                <Route path="/behandlungen-info" element={<TreatmentInfoPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/registrieren" element={<RegisterPage />} />
              </Route>

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/termine" element={<AdminDashboard section="appointments" />} />
                <Route path="/admin/behandlungen" element={<AdminDashboard section="treatments" />} />
                <Route path="/admin/produkte" element={<AdminDashboard section="products" />} />
                <Route path="/admin/bestellungen" element={<AdminDashboard section="orders" />} />
                <Route path="/admin/blog" element={<AdminDashboard section="blog" />} />
                <Route path="/admin/kunden" element={<AdminDashboard section="customers" />} />
                <Route path="/admin/einstellungen" element={<AdminDashboard section="settings" />} />
              </Route>
            </Routes>
          </Suspense>
          <Toaster position="bottom-right" richColors />
          <CookieBanner />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
